import React, { useState, useEffect } from "react";
import {isWithinInterval, subWeeks, startOfDay, endOfDay} from "date-fns";
import axios from "../axios/axios";
import TaskForm from "../components/TaskForm";
import { format } from "date-fns";
import { Text, FormControl, FormLabel, Input, Alert, AlertIcon } from "@chakra-ui/react";
import { useTasks } from "../providers/TasksProvider";
import parseTimeInput from "../lib/helpers/parseTimeInput";

function AdminTaskFormContainer({ user }) {
  const [clients, setClients] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [errorMessage, setErrorMessage] = useState("");
  const [taskData, setTaskData] = useState({
    clientId: "",
    projectId: "",
    taskId: "",
    timeSpent: "",
    taskDescription: "",
  });

  const { fetchTasks: fetchUserTasks } = useTasks();

  // Calcul des bornes de date
  const currentDate = endOfDay(new Date());
  const oneWeekAgo = startOfDay(subWeeks(currentDate, 1));

  const isDateInCurrentWeek = isWithinInterval(selectedDate, {
    start: oneWeekAgo,
    end: currentDate,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientsResponse = await axios.get("/allClients");

        if (clientsResponse.data && clientsResponse.data.length > 0) {
          setClients(clientsResponse.data);
        } else {
          console.warn("Aucun client reçu de l'API");
          setErrorMessage("Aucun client disponible");
        }

        const tasksResponse = await axios.get(`/tasks?user_job_id=${user.user_job_id}`);
        if (tasksResponse.data && tasksResponse.data.length > 0) {
          setTasks(tasksResponse.data);
        } else {
          console.warn("Aucune tâche reçue de l'API");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        setErrorMessage("Erreur lors du chargement des données");
      }
    };

    fetchData();
  }, [user.user_job_id]);

  const fetchClientsProjects = clientsId => {
    axios
      .get(`/project?client_id=${clientsId}`)
      .then(response => {
        setClientProjects(response.data);

        const selectedClient = clients.find(client => client.id === parseInt(clientsId, 10));

        if (selectedClient) {
          setTaskData(prevTaskData => ({
            ...prevTaskData,
            clientName: selectedClient.client_name,
          }));
        }
      })
      .catch(error => console.error("Error fetching client's projects:", error));
  };

  const handleClientChange = async e => {
    const selectedClientId = e.target.value;

    setTaskData(prevTaskData => ({
      ...prevTaskData,
      clientId: selectedClientId,
      clientName: "",
      projectName: "",
    }));

    if (selectedClientId) {
      fetchClientsProjects(selectedClientId);
    } else {
      setClientProjects([]);
    }
  };

  const handleProjectChange = e => {
    const selectedProjectId = e.target.value;
    if (selectedProjectId) {
      const selectedProject = clientProjects.find(project => project.id === parseInt(selectedProjectId, 10));

      if (selectedProject) {
        setTaskData(prevTaskData => ({
          ...prevTaskData,
          projectId: selectedProjectId,
          projectName: selectedProject.id,
        }));
      }
    } else {
      setTaskData(prevTaskData => ({
        ...prevTaskData,
        projectId: "",
        projectName: "",
      }));
    }
  };

  const handleTaskChange = e => {
    const { name, value } = e.target;

    setTaskData(prevTaskData => {
      if (name === "taskId") {
        const selectedTask = tasks.find(task => task.id === parseInt(value, 10));

        return {
          ...prevTaskData,
          taskId: value,
          task_name: selectedTask ? selectedTask.task_name : "",
          task_code: selectedTask ? selectedTask.task_code : "",
          taskDescription: "",
        };
      }

      if (name === "taskDescription") {
        return {
          ...prevTaskData,
          taskDescription: value,
        };
      }

      return {
        ...prevTaskData,
        [name]: value,
      };
    });
  };

  const handleDateChange = date => {
    setSelectedDate(date);
  };

  const handleAddTask = () => {
    const timeInHours = parseTimeInput(taskData.timeSpent);
    const timeSpent = timeInHours * 60;

    if (!taskData.clientId || !taskData.projectId || !taskData.taskId || !taskData.timeSpent || timeSpent <= 0) {
      setErrorMessage("Vous devez remplir tous les champs !");
      return;
    }

    if (taskData.task_code === "9999" && !taskData.taskDescription) {
      setErrorMessage("Une description est requise pour cette tâche !");
      return;
    }

    setErrorMessage("");

    axios
      .post("/user_task", {
        user_id: user.id,
        user_email: user.email,
        task_id: parseInt(taskData.taskId),
        task_name: taskData.task_name,
        task_code: taskData.task_code,
        task_description: taskData.task_code === "9999" ? taskData.taskDescription : null,
        time_spent: timeSpent,
        client: taskData.clientName,
        projet: parseInt(taskData.projectId),
        date: format(selectedDate, "yyyy-MM-dd"),
      })
      .then(response => {
        // setTaskData({
        //   clientId: "",
        //   projectId: "",
        //   taskId: "",
        //   timeSpent: "",
        //   taskDescription: "",
        // });
        fetchUserTasks();
      })
      .catch(error => {
        console.error("Error posting time spent:", error);
        setErrorMessage(error.response?.data || "Erreur lors de l'ajout de la tâche");
      });
  };

  return (
    <>
      <FormControl mb={4}>
        <FormLabel>Sélectionner une date</FormLabel>
        <Input
          type="date"
          value={format(selectedDate, "yyyy-MM-dd")}
          onChange={e => handleDateChange(new Date(e.target.value))}
          min={format(oneWeekAgo, "yyyy-MM-dd")}
          max={format(currentDate, "yyyy-MM-dd")}
        />
      </FormControl>
      {!isDateInCurrentWeek && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          Cette date n'est pas dans la période attendue
        </Alert>
      )}
      {errorMessage && <Text color="red">{errorMessage}</Text>}
      <TaskForm
        clients={clients || []}
        clientProjects={clientProjects || []}
        tasks={tasks || []}
        taskData={taskData}
        selectedDate={selectedDate}
        handleClientChange={handleClientChange}
        handleProjectChange={handleProjectChange}
        handleTaskChange={handleTaskChange}
        handleDateChange={handleDateChange}
        handleAddTask={handleAddTask}
        isDateInCurrentWeek={isDateInCurrentWeek}
        errorMessage={errorMessage}
      />
    </>
  );
}

export default AdminTaskFormContainer;
